import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TableCell, TableRow, Typography } from "@mui/material";
import { Add, Verified } from "@mui/icons-material";

import TruncateText from "../../components/TruncateText";
import SearchInput from "../../components/SearchInput";
import Table from "../../components/Table";
import Pagination from "../../components/Pagination";
import EmptyState from "../../components/EmptyState";
import Tabs from "../../components/Tabs";
import messages from "../../assets/locale/messages";
import noRequestImg from "../../assets/images/placeholders/noProductImagePlacholder.svg";
import noImporterImg from "../../assets/images/placeholders/noImportersPlaceholderImg.svg";
import Button from "../../components/Button";
import {
  getVoucherRequestsListRequest,
  getVouchersUsedNotUsedListRequest,
  getVouchersUsedNotUsedListResponse
} from "../../store/Vouchers/actions";
import { formatDate, RandomCodeGenerator } from "../../utils/Helpers";
import GenerateVoucherModal from "./GenerateVoucherModal";
import { showHideLoader } from "../../store/Loader/actions";
import { hasPermission, PERMISSIONS_IDS } from "../../utils/Roles";
import "./VouchersList.scss";

const VouchersList = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const { vouchers, shared } = messages[lang];

  const generatedVoucherCode = RandomCodeGenerator({
    length: 6
  });

  const voucherRequestsLists = useSelector(
    (state) => state.vouchers.voucherRequests.list
  );
  const voucherRequestsMeta = useSelector(
    (state) => state.vouchers.voucherRequests.meta
  );
  const voucherUsedNotUsedList = useSelector(
    (state) => state.vouchers.voucherUsedNotUsed.list
  );
  const voucherUsedNotUsedMeta = useSelector(
    (state) => state.vouchers.voucherUsedNotUsed.meta
  );

  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [isGenerateVoucherModalOpen, setIsGenerateVoucherModalOpen] =
    useState(false);
  const [companyData, setCompanyData] = useState({});

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const getRequestsList = () => {
    dispatch(
      getVoucherRequestsListRequest({
        params: {
          page,
          search_value: searchValue
        }
      })
    );
  };
  const getUsedNotUsedList = () => {
    dispatch(
      getVouchersUsedNotUsedListRequest({
        params: {
          page,
          search_value: searchValue,
          isUsed: tabValue === 2
        }
      })
    );
  };

  useEffect(() => {
    dispatch(showHideLoader(true));
    switch (tabValue) {
      case 0:
        getRequestsList();
        break;
      case 1:
      case 2:
        dispatch(
          getVouchersUsedNotUsedListResponse({ list: [], meta: {} })
        );
        getUsedNotUsedList();
        break;
      default:
        break;
    }
    setSearchValue("");
    setPage(1);
  }, [tabValue]);

  useEffect(() => {
    tabValue === 0 ? getRequestsList() : getUsedNotUsedList();
  }, [page, searchValue, isGenerateVoucherModalOpen]);

  const renderRequestTableRow = ({
    Id,
    CompanyLogo,
    CompanyName,
    Code,
    Percent,
    CreationDate,
    RequestDate,
    CreationConsumptionDate,
    UserId
  }) => (
    <TableRow
      key={`request-row-${Id}`}
      className={`${tabValue === 0 && "voucher-requests-table"}`}>
      <TableCell style={{ width: "50%" }}>
        <div className="d-flex align-items-center">
          <img
            src={CompanyLogo || noRequestImg}
            onError={onImageError}
            alt={"company-logo"}
            className="company-img faint-grey-border border-radius-8 me-3"
          />
          <TruncateText text={CompanyName || "__"} />
          <Verified
            color="primary"
            className="ms-1"
            sx={{ width: "12px" }}
          />
        </div>
      </TableCell>
      {tabValue !== 0 && (
        <TableCell>
          <TruncateText text={Code || "__"} />
        </TableCell>
      )}
      {tabValue !== 0 && (
        <TableCell>
          {Percent || "__"}
          {!!Percent && "%"}
        </TableCell>
      )}
      <TableCell style={{ width: "50%" }}>
        {(CreationDate && formatDate(CreationDate)) ||
          (RequestDate && formatDate(RequestDate)) ||
          (CreationConsumptionDate &&
            formatDate(CreationConsumptionDate)) ||
          "__"}
      </TableCell>
      {tabValue === 0 &&
        hasPermission(PERMISSIONS_IDS.vouchers_generate) && (
          <TableCell style={{ width: "50%" }}>
            <Button
              outlined
              label={vouchers.generateButton}
              onClick={() => {
                setCompanyData({
                  companyId: Id,
                  companyName: CompanyName,
                  userId: UserId,
                  voucherCode: generatedVoucherCode,
                  tabValue,
                  isRequest: true,
                  params: {
                    page: 1,
                    search_value: searchValue
                  }
                });
                setIsGenerateVoucherModalOpen(true);
              }}
              labelClass={"fsize-14"}
              customSpacing={" py-2"}
            />
          </TableCell>
        )}
    </TableRow>
  );

  const renderTabContent = () => (
    <div>
      <div className="table-header my-4">
        <SearchInput
          name="vouchers-search"
          id="vouchers-search"
          className="vouchers-search"
          value={searchValue}
          inputWrapperClass="search-input-wrapper search-input-wrapper-width"
          placeholder={vouchers.list.searchPlaceholder}
          onChange={(value) => {
            setPage(1);
            setSearchValue(value);
          }}
        />
      </div>
      <div
        className={`${tabValue === 0 ? "requests-table" : tabValue === 1 ? "not-used-table" : "used-table"} table-content`}>
        {(
          tabValue === 0
            ? !!voucherRequestsLists?.length
            : !!voucherUsedNotUsedList?.length
        ) ? (
          <Table
            className={"px-0"}
            headlines={[
              vouchers.list.companyName,
              tabValue !== 0 && vouchers.list.voucherCode,
              tabValue !== 0 && vouchers.list.percentage,
              tabValue === 0
                ? vouchers.list.requestDate
                : tabValue === 1
                  ? vouchers.list.creationDate
                  : vouchers.list.consumptionDate,
              tabValue === 0 &&
                hasPermission(PERMISSIONS_IDS.vouchers_generate) &&
                shared.actions
            ].filter(Boolean)}
            rows={
              !isLoading &&
              (tabValue === 0
                ? !!voucherRequestsLists?.length &&
                  voucherRequestsLists.map((request) =>
                    renderRequestTableRow(request)
                  )
                : !!voucherUsedNotUsedList?.length &&
                  voucherUsedNotUsedList.map((request) =>
                    renderRequestTableRow(request)
                  ))
            }
          />
        ) : (
          <div>
            {!isLoading && (
              <EmptyState
                subTitle={
                  searchValue
                    ? shared.noSearchResults
                    : vouchers.list.noDataPlaceholder
                }
                className="empty-state-container"
                imgSrc={noImporterImg}
              />
            )}
          </div>
        )}
      </div>
      <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
        {!isLoading &&
          (tabValue === 0
            ? voucherRequestsLists?.length > 0
            : voucherUsedNotUsedList.length > 0) && (
            <Pagination
              count={
                tabValue === 0
                  ? voucherRequestsMeta.pages
                  : voucherUsedNotUsedMeta.pages
              }
              page={
                tabValue === 0
                  ? voucherRequestsMeta.currentPage
                  : voucherUsedNotUsedMeta.currentPage || page
              }
              handleChange={(page) => {
                setPage(page);
              }}
              defaultPage={1}
            />
          )}
      </div>
    </div>
  );

  const tabsArray = [
    {
      label: vouchers.voucherType.Requests,
      content: renderTabContent()
    },
    {
      label: vouchers.voucherType.notUsed,
      content: renderTabContent()
    },
    {
      label: vouchers.voucherType.used,
      content: renderTabContent()
    }
  ];

  return (
    <div className="vouchers-container">
      <div className="page-title pb-4 d-flex justify-content-between align-items-center">
        <div className="title-container d-flex justify-content-center align-items-center">
          <Typography variant="h5" className="title-color me-3">
            {vouchers.list.title}
          </Typography>
        </div>
        {hasPermission(PERMISSIONS_IDS.vouchers_generate) && (
          <div className="title-buttons-container">
            <Button
              label={
                <span className="d-flex align-items-center">
                  <Add fontSize="medium" />
                  <span className="ps-1 d-block">
                    {vouchers.list.generateVoucherButton}
                  </span>
                </span>
              }
              onClick={() => {
                setCompanyData({
                  voucherCode: generatedVoucherCode,
                  isRequest: false,
                  tabValue,
                  params: {
                    page: 1,
                    search_value: searchValue,
                    isUsed: tabValue === 0 ? null : tabValue === 2
                  }
                });
                setIsGenerateVoucherModalOpen(true);
              }}
              customSpacing={"px-2 px-md-4"}
            />
          </div>
        )}
      </div>
      <div className="vouchers-table-container bg-white d-flex flex-column w-100  p-4 bg-white border-radius-8 mb-4">
        <div className="tabs-section">
          <Tabs
            tabs={tabsArray}
            value={tabValue}
            handleChange={setTabValue}
            activeTabAdornment={
              isLoading ? null : (
                <span className="records-count faint-blue-bg border-radius-50 p-2">
                  {tabValue === 0
                    ? voucherRequestsMeta.count
                    : voucherUsedNotUsedMeta.count || 0}
                </span>
              )
            }
          />
        </div>
      </div>
      {isGenerateVoucherModalOpen && (
        <GenerateVoucherModal
          isOpen={isGenerateVoucherModalOpen}
          companyData={companyData}
          onClose={() => {
            setIsGenerateVoucherModalOpen(false);
            setCompanyData({});
          }}
        />
      )}
    </div>
  );
};

export default VouchersList;
